import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate, convertObjectDates } from '../../../../utils/firebase-timestamp-transformation';
import './EmployeeRewardTab.scss';

const EmployeeRewardTab = () => {
  const [loading, setLoading] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [migrated, setMigrated] = useState([]);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    requiresMigration: 0,
    migrated: 0,
    failed: 0
  });
  const [initialized, setInitialized] = useState(false);

  // Define timestamp fields to convert
  const timestampFields = ['date'];

  // Fetch employee rewards from Firestore
  const fetchRewards = async () => {
    // Prevent multiple fetches if already loading
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching employee rewards...');
      
      // Access the employeeReward collection directly
      const rewardsRef = collection(db, 'otherDBs', 'employeeReward', 'employeeReward');
      const querySnapshot = await getDocs(rewardsRef);
      
      console.log(`Found ${querySnapshot.size} employee rewards`);
      
      const rewardsData = [];
      querySnapshot.forEach((doc) => {
        // Convert timestamps to Date objects
        const data = convertObjectDates(doc.data(), timestampFields);
        
        rewardsData.push({
          id: doc.id,
          ...data,
        });
      });
      
      console.log('Processed employee rewards:', rewardsData.length);
      
      // Process the data to identify which rewards need migration
      const processed = rewardsData.map(reward => {
        // Check if employeeEmail needs migration
        const needsEmployeeEmailMigration = reward.employeeEmail && 
          emailChanges.find(change => change.oldEmail === reward.employeeEmail);
        
        // Check if employeeId needs migration (if it's an email)
        const needsEmployeeIdMigration = reward.employeeId && 
          reward.employeeId.includes('@') && 
          emailChanges.find(change => change.oldEmail === reward.employeeId);
        
        return {
          ...reward,
          requiresMigration: Boolean(needsEmployeeEmailMigration || needsEmployeeIdMigration),
          employeeEmailChange: needsEmployeeEmailMigration,
          employeeIdChange: needsEmployeeIdMigration,
          migrated: false,
          error: null
        };
      });
      
      // Update stats
      setStats({
        total: processed.length,
        requiresMigration: processed.filter(reward => reward.requiresMigration).length,
        migrated: 0,
        failed: 0
      });
      
      setRewards(processed);
      setInitialized(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee rewards:', error);
      setError('Failed to fetch employee rewards: ' + error.message);
      setInitialized(true);
      setLoading(false);
    }
  };

  // Migrate a single employee reward
  const migrateReward = async (reward) => {
    try {
      const rewardRef = doc(db, 'otherDBs', 'employeeReward', 'employeeReward', reward.id);
      const updateData = {};

      // Update employeeEmail if needed
      if (reward.employeeEmailChange) {
        updateData.employeeEmail = reward.employeeEmailChange.newEmail;
      }

      // Update employeeId if needed (only if it's an email)
      if (reward.employeeIdChange) {
        updateData.employeeId = reward.employeeIdChange.newEmail;
      }

      // Only update if there are changes to make
      if (Object.keys(updateData).length > 0) {
        await updateDoc(rewardRef, updateData);
        return { success: true };
      } else {
        return { success: true, noChangesNeeded: true };
      }
    } catch (error) {
      console.error(`Error migrating employee reward ${reward.id}:`, error);
      return { success: false, error: error.message };
    }
  };

  // Migrate all rewards that require migration
  const migrateAllRewards = async () => {
    if (!rewards || rewards.length === 0) return;
    
    setLoading(true);
    const updatedRewards = [...rewards];
    let migratedCount = 0;
    let failedCount = 0;
    
    for (let i = 0; i < updatedRewards.length; i++) {
      const reward = updatedRewards[i];
      
      if (reward.requiresMigration && !reward.migrated) {
        const result = await migrateReward(reward);
        
        updatedRewards[i] = {
          ...reward,
          migrated: result.success,
          error: result.success ? null : result.error
        };
        
        if (result.success) {
          migratedCount++;
        } else {
          failedCount++;
        }
      }
    }
    
    // Update stats
    setStats(prev => ({
      ...prev,
      migrated: prev.migrated + migratedCount,
      failed: prev.failed + failedCount
    }));
    
    setRewards(updatedRewards);
    setMigrated(updatedRewards.filter(reward => reward.migrated));
    setLoading(false);
  };

  // Format date timestamp for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestampToDate(timestamp);
    return date ? date.toLocaleString() : 'Invalid Date';
  };

  // Truncate long text for display
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Only run fetchRewards once on component mount
  useEffect(() => {
    if (!initialized && !loading) {
      fetchRewards();
    }
  }, [initialized, loading]);

  return (
    <div className="employee-reward-tab tab-content-container">
      <div className="tab-header">
        <h2>Employee Reward Migration</h2>
        <p>Migrate email addresses in employee rewards from myelinh.com to myelinz.com domain.</p>
      </div>
      
      <div className="migration-stats">
        <div className="stat-item">
          <span className="stat-label">Total Rewards:</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Requires Migration:</span>
          <span className="stat-value">{stats.requiresMigration}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Migrated:</span>
          <span className="stat-value">{stats.migrated}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Failed:</span>
          <span className="stat-value">{stats.failed}</span>
        </div>
      </div>
      
      <div className="migration-actions">
        <button 
          className="fetch-btn" 
          onClick={fetchRewards} 
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh Data'}
        </button>
        <button 
          className="migrate-btn" 
          onClick={migrateAllRewards} 
          disabled={loading || stats.requiresMigration === 0 || stats.migrated === stats.requiresMigration}
        >
          {loading ? 'Migrating...' : 'Migrate All'}
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="data-table-container">
        <h3>Employee Rewards Requiring Migration</h3>
        {loading ? (
          <div className="loading-indicator">Loading...</div>
        ) : (
          <>
            {rewards.length === 0 ? (
              <div className="no-data-message">No employee rewards found</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Employee Name</th>
                    <th>Employee Email</th>
                    <th>Employee ID</th>
                    <th>Reward Type</th>
                    <th>Percentage</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rewards.filter(reward => reward.requiresMigration).map(reward => (
                    <tr key={reward.id} className={reward.migrated ? 'migrated' : (reward.error ? 'error' : '')}>
                      <td title={reward.id}>{truncateText(reward.id, 10)}</td>
                      <td title={reward.employeeName}>{truncateText(reward.employeeName, 20)}</td>
                      <td title={reward.employeeEmail || 'N/A'}>
                        {reward.employeeEmail || 'N/A'}
                        {reward.employeeEmailChange && (
                          <span className="migration-info">→ {reward.employeeEmailChange.newEmail}</span>
                        )}
                      </td>
                      <td title={reward.employeeId || 'N/A'}>
                        {truncateText(reward.employeeId, 15) || 'N/A'}
                        {reward.employeeIdChange && (
                          <span className="migration-info">→ {truncateText(reward.employeeIdChange.newEmail, 15)}</span>
                        )}
                      </td>
                      <td>{reward.rewardType || 'N/A'}</td>
                      <td>{reward.percentage || 'N/A'}</td>
                      <td>{formatDate(reward.date)}</td>
                      <td>
                        {reward.migrated ? (
                          <span className="status-success">Migrated</span>
                        ) : reward.error ? (
                          <span className="status-error" title={reward.error}>Failed</span>
                        ) : (
                          <span className="status-pending">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rewards.filter(reward => reward.requiresMigration).length === 0 && (
                    <tr>
                      <td colSpan="8" className="no-data">No employee rewards require migration</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
      
      {migrated.length > 0 && (
        <div className="migration-results">
          <h3>Successfully Migrated Rewards</h3>
          <div className="results-count">Total: {migrated.length}</div>
          <ul className="results-list">
            {migrated.map(reward => (
              <li key={reward.id}>
                <div className="reward-title">{reward.employeeName} - {reward.rewardType}</div>
                {reward.employeeEmailChange && (
                  <div>
                    Employee Email: {reward.employeeEmail} → {reward.employeeEmailChange.newEmail}
                  </div>
                )}
                {reward.employeeIdChange && (
                  <div>
                    Employee ID: {reward.employeeId} → {reward.employeeIdChange.newEmail}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EmployeeRewardTab; 